import { Analytics } from '@cobliteam/analytics';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useLanguage } from '@cobliteam/i18n';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { getTextForLanguage } from '@cobliteam/ui-business/utils';
import { Fragment, useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './Collapsible';
import LinkWrapper from './LinkWrapper';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu as SidebarMenuBase,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from './Sidebar';

export function SidebarMenuMobile() {
  const { sidebar } = useDashboard();
  const { currentLanguage } = useLanguage();
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({});

  const router = sidebar.router;

  const handleGroupToggle = (title: string, isOpen: boolean) => {
    Analytics.track('Sidebar - Group - Toggle', { title, isOpen });
    setOpenGroups((prev) => ({
      ...prev,
      [title]: isOpen,
    }));
  };

  return (
    <>
      {router?.map((router, index) => {
        if (router.sidebar?.hide) return null;
        const routerTitle = getTextForLanguage(router.sidebar?.title, currentLanguage);
        // if the nav item is not a group, show a simple menu item
        if (!router.routes) {
          return (
            <SidebarGroup key={routerTitle}>
              <SidebarMenuBase>
                <SidebarMenuItem>
                  <LinkWrapper to={router?.url ?? '#'} pageId={router.id} onClick={() => Analytics.track('Sidebar - Navigate', { destination: routerTitle })}>
                    <SidebarMenuButton tooltip={routerTitle} size='sm' className='text-sidebar-menu' isActive={router.sidebar?.active}>
                      {router.sidebar?.icon && <Icon name={router.sidebar?.icon as IconNames} />}
                      <span>{routerTitle}</span>
                    </SidebarMenuButton>
                  </LinkWrapper>
                </SidebarMenuItem>
              </SidebarMenuBase>
            </SidebarGroup>
          );
        }

        return (
          <SidebarGroup key={routerTitle} className={cn('mt-2', index > 1 && 'mt-4')}>
            <SidebarGroupLabel data-testid='sidebar-section-title' className='!text-gray-8 text-sidebar-mobile-section'>
              {routerTitle}
            </SidebarGroupLabel>
            {router.routes?.map((route: any, index: any) => {
              if (route.sidebar?.hide) return null;
              const isActive = route.sidebar?.active;
              const routeTitle = getTextForLanguage(route.sidebar?.title, currentLanguage);
              return (
                <Fragment key={routeTitle}>
                  {route?.routes?.some((item: any) => !item.sidebar?.hide) ? (
                    <SidebarGroup key={routeTitle}>
                      <Collapsible
                        key={`${routeTitle} ${index}`}
                        className='group/collapsible'
                        open={openGroups[routeTitle || '']}
                        onOpenChange={(isOpen) => handleGroupToggle(routeTitle || '', isOpen)}
                      >
                        <SidebarGroupLabel asChild>
                          <CollapsibleTrigger className='w-full text-sidebar-mobile-menu text-white text-xs hover:bg-sidebar-mobile-accent hover:text-sidebar-mobile-accent-foreground'>
                            <div className='flex w-full items-center justify-between gap-2'>
                              <div className='flex items-center gap-2'>
                                {route.sidebar?.icon && <Icon name={route.sidebar?.icon} />}
                                {routeTitle}
                              </div>
                              <Icon name='chevron-down' className='ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180' />
                            </div>
                          </CollapsibleTrigger>
                        </SidebarGroupLabel>
                        <CollapsibleContent>
                          <SidebarGroupContent>
                            <SidebarMenuSub>
                              {route?.routes?.map((item: any) => {
                                if (item.hide) return null;
                                const isActive = item.sidebar?.active;
                                const goTo = item.redirect || item?.url || '#';
                                const itemTitle = getTextForLanguage(item.sidebar?.title, currentLanguage);
                                return (
                                  <SidebarMenuSubItem key={itemTitle}>
                                    <SidebarMenuSubButton asChild isActive={isActive} className='text-sidebar-mobile-menu text-white text-xs'>
                                      <LinkWrapper to={goTo} pageId={item.id} onClick={() => Analytics.track('Sidebar - Navigate', { destination: itemTitle })}>
                                        {itemTitle}
                                      </LinkWrapper>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                );
                              })}
                            </SidebarMenuSub>
                          </SidebarGroupContent>
                        </CollapsibleContent>
                      </Collapsible>
                    </SidebarGroup>
                  ) : (
                    <SidebarGroup key={routeTitle}>
                      <SidebarMenuBase>
                        <SidebarMenuItem>
                          <LinkWrapper
                            to={route.redirect || route?.url || '#'}
                            pageId={route.id}
                            onClick={() => Analytics.track('Sidebar - Navigate', { destination: routeTitle })}
                          >
                            <SidebarMenuButton tooltip={routeTitle} size='sm' className='text-sidebar-mobile-menu' isActive={isActive}>
                              {route.sidebar?.icon && <Icon name={route.sidebar?.icon} />}
                              <span>{routeTitle}</span>
                            </SidebarMenuButton>
                          </LinkWrapper>
                        </SidebarMenuItem>
                      </SidebarMenuBase>
                    </SidebarGroup>
                  )}
                </Fragment>
              );
            })}
          </SidebarGroup>
        );
      })}
    </>
  );
}
